// =====================================================================
// Assessment Center — Instructor Registration.
// Fields: Full Name, Institution (optional), Email, Password, Confirm.
// After success, auto-signs in and redirects to the Instructor Dashboard.
// =====================================================================
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthShell from "./AuthShell";
import { Button, Field, Input } from "../../shared/components/ui";
import { useAuth } from "../../shared/auth/AuthContext";

export default function InstructorRegisterPage() {
  const { instructorRegister } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [institution, setInstitution] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await instructorRegister({ fullName, institution: institution || undefined, email, password });
      navigate("/instructor/assessment-center");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't create your account.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell title="Register as Instructor" subtitle="Set up your instructor account.">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Full Name" required>
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Dr. Sam Whitfield" required />
        </Field>
        <Field label="Institution" hint="Optional.">
          <Input value={institution} onChange={(e) => setInstitution(e.target.value)} placeholder="Riverside Flight Academy" />
        </Field>
        <Field label="Email" required>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@school.edu" required />
        </Field>
        <Field label="Password" required hint="At least 6 characters.">
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
        </Field>
        <Field label="Confirm Password" required>
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
          />
        </Field>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? "Creating account…" : "Create Account"}
        </Button>
      </form>

      <div className="mt-6 border-t border-border-soft pt-5 text-center">
        <p className="text-sm text-slate-500">
          Already registered?{" "}
          <Link to="/login/instructor" className="font-medium text-navy-700 hover:underline">
            Log in
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
